import DateTimeTool from './DateTimeTool';
import { TYPES, toString, isInteger, } from './utils';
export enum LOG_LEVEL { ALL, TRACE, DEBUG, INFO, WARN, ERROR, FATAL, OFF, };
type Listener = (message: string) => void;

export class Logger {
  static LOG_LEVEL = LOG_LEVEL;

  /**
   * 大于等于当前等级的才会打印
   */
  private level: LOG_LEVEL = LOG_LEVEL.OFF;
  private listenHandle: Listener[] = [];
  private static instance: Logger = new Logger();
  private constructor() {}

  /**
   * 格式化日志信息
   */
  private formatMessage(level: LOG_LEVEL, message: string): string {
    let logLevel: string = LOG_LEVEL[level];
    let time: string = DateTimeTool.dateTimeFormat(new Date());

    return `[${logLevel}] ${time} ${message}`;
  }
  /**
   * 处理参数
   * 1. 如果是字符串，直接返回
   * 2. 如果是undefined 或者 null，改成字符串
   * 3. 如果是Error,打印message 和 stack
   * 4. 如果是Date类型，打印时间戳
   * 5. 如果是对象，JSON.stringify
   */
  private getParameters(args: any[]): string {
    return args.map(param => {
      let paramType = toString(param);
      if ([TYPES.FUNCTION, TYPES.STRING, TYPES.UNDEFINED, TYPES.NULL].includes(paramType)) {
        return String(param);
      }
  
      if (typeof Symbol != typeof void 0) {
        if (TYPES.SYMBOL == paramType) {
          return param.toString();
        }
      }

      if (TYPES.NUMBER == paramType) {
        return param;
      }

      if (TYPES.ERROR == paramType) {
        return `${param.message}: \n${param.stack}`;
      }
  
      if (TYPES.DATE == paramType) {
        return `date: ${param.getTime()}`;
      }
  
      if (TYPES.ARRAY == paramType) {
        let result = [];
        result.push('[');

        for (let prop in param) {
          if (isInteger(prop)) {
            result.push(`${this.getParameters([param[prop]])}, `);
          } else {
            result.push(`${prop}: ${this.getParameters([param[prop]])}, `);
          }
        }

        result.push(`length: ${param.length}`);

        result.push(']');

        return result.join('');
      }

      if (TYPES.OBJECT == paramType) {
        let result = [];
        result.push('{');
        for (let prop in param) {
          result.push(`${prop} => ${this.getParameters([param[prop]])}, `);
        }

        // 移掉最后多余的,
        if (1 < result.length) {
          result[result.length - 1] = result[result.length - 1].slice(0, -2);
        }
        result.push('}');
        return result.join('');
      }

      try {
        return JSON.stringify(param);
      } catch(e) {
        return param.toString();
      }
    }).join(' ');
  }

  /**
   * 内部发布消息，给订阅的函数
   */
  private publish(message: string): void {
    this.listenHandle.forEach(handle => {
      try {
        handle(message);
      } catch (e) {
        console.log(e);
      }
    });
  }

  /**
   * 单例工厂,获取logger实例
   */
  static getLogger(): Logger {
    return this.instance;
  }

  setLevel(level: LOG_LEVEL): void {
    this.level = level;
  }

  subscribe(listener: Listener): void {
    this.listenHandle.push(listener);
  }

  unsubscribe(listener: Listener): void {
    for (let i = 0; i < this.listenHandle.length; i++) {
      if (this.listenHandle[i] == listener) {
        this.listenHandle.splice(i, 1);
        break;
      }
    }
  }

  trace(...message: any[]): void {
    const CURRENT_LEVEL: number = LOG_LEVEL.TRACE;

    if (CURRENT_LEVEL >= this.level) {
      this.publish(this.formatMessage(CURRENT_LEVEL, this.getParameters(message)));
    }
  }

  info(...message: any[]): void {
    const CURRENT_LEVEL: number = LOG_LEVEL.INFO;

    if (CURRENT_LEVEL >= this.level) {
      this.publish(this.formatMessage(CURRENT_LEVEL, this.getParameters(message)));
    }
  }

  debug(...message: any[]): void {
    const CURRENT_LEVEL: number = LOG_LEVEL.DEBUG;

    if (CURRENT_LEVEL >= this.level) {
      this.publish(this.formatMessage(CURRENT_LEVEL, this.getParameters(message)));
    }
  }

  warn(...message: any[]): void {
    const CURRENT_LEVEL: number = LOG_LEVEL.WARN;

    if (CURRENT_LEVEL >= this.level) {
      this.publish(this.formatMessage(CURRENT_LEVEL, this.getParameters(message)));
    }
  }

  error(...message: any[]): void {
    const CURRENT_LEVEL: number = LOG_LEVEL.ERROR;

    if (CURRENT_LEVEL >= this.level) {
      this.publish(this.formatMessage(CURRENT_LEVEL, this.getParameters(message)));
    }
  }

  fatal(...message: any[]): void {
    const CURRENT_LEVEL: number = LOG_LEVEL.FATAL;

    if (CURRENT_LEVEL >= this.level) {
      this.publish(this.formatMessage(CURRENT_LEVEL, this.getParameters(message)));
    }
  }
}

export default Logger;
